import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  Home,
  Building,
  Ruler,
  CheckCircle,
  Phone,
  Award,
  Clock,
  MapPin,
  Star,
  Quote,
  Mail,
  Paintbrush,
  Hammer,
  Shield
} from "lucide-react";

// Import drywall images
import drywallFinishing from "@/assets/drywall-finishing-professional.jpg";
import drywallInstallation from "@/assets/drywall-installation-work.jpg";
import drywallSmooth from "@/assets/drywall-smooth-finish.jpg";
import drywallTools from "@/assets/drywall-tools-equipment.jpg";

const DrywallServices = () => {
  const faqData = [
    {
      question: "What drywall services do you provide in Niagara Region, Hamilton, and Toronto?",
      answer: "Blue Knight Contracting provides comprehensive drywall services including drywall installation, taping, mudding, sanding, texture application, repair, and finishing for residential, commercial, and industrial properties across Niagara Region, Hamilton, and Toronto GTA. We work with standard 1/2\" and 5/8\" drywall, moisture-resistant drywall for bathrooms, and fire-rated drywall for code compliance."
    },
    {
      question: "How much does drywall installation cost in Toronto and Hamilton?",
      answer: "Drywall installation costs vary based on project size, ceiling height, and finish level. On average, expect $1.50-$3.00 per square foot for standard installation and finishing in Toronto, Hamilton, and Niagara Region. We provide free detailed quotes that include materials (drywall sheets, joint compound, tape), labor, and finishing. Factors affecting cost include room size, number of corners, ceiling work, and desired texture or finish level (Level 3, 4, or 5)."
    },
    {
      question: "What are the different drywall finish levels?",
      answer: "Drywall finish levels range from Level 0 to Level 5. Level 3 (basic finish) is suitable for heavy texture or tile areas. Level 4 (standard finish) is most common for flat paint and light textures. Level 5 (premium finish) provides the smoothest surface for critical lighting areas and gloss paints. We recommend Level 4 for most residential applications in Niagara, Hamilton, and Toronto homes, and Level 5 for high-end finishes."
    },
    {
      question: "Can you repair damaged drywall and match existing textures?",
      answer: "Yes. Our skilled drywall finishers repair holes, cracks, water damage, and impact damage throughout Niagara Region, Hamilton, and Toronto GTA. We can match existing textures including orange peel, knockdown, skip trowel, and popcorn ceiling textures. Repairs are blended seamlessly to match surrounding areas, making damage invisible after painting."
    },
    {
      question: "Do you provide drywall services for commercial projects?",
      answer: "Absolutely. Blue Knight Contracting provides commercial drywall installation and finishing for offices, retail spaces, restaurants, medical facilities, and industrial buildings in Hamilton, Toronto, and Niagara Region. We handle large-scale projects, work around business schedules, and ensure fire-rated and sound-rated assemblies meet building code requirements."
    },
    {
      question: "How long does drywall installation and finishing take?",
      answer: "Timeline depends on project size. A standard room (12x12) typically takes 2-3 days including installation, taping, mudding (multiple coats), sanding, and final finish. Larger projects like whole-home drywall in Niagara, Hamilton, or Toronto may take 1-2 weeks. Each mud coat requires 24 hours drying time. We provide realistic timelines during your free consultation."
    },
    {
      question: "What's the difference between drywall taping and mudding?",
      answer: "Taping is the process of applying paper or fiberglass mesh tape over drywall seams to reinforce joints. Mudding (applying joint compound) covers the tape and fasteners in multiple coats. First coat embeds tape, second coat (24 hours later) widens the joint, third coat feathers edges for smooth transitions. Professional taping and mudding creates invisible seams that disappear after painting."
    },
    {
      question: "Do you install moisture-resistant drywall for bathrooms?",
      answer: "Yes. We install moisture-resistant (green board) and mold-resistant (purple board) drywall for bathrooms, laundry rooms, and basements throughout Hamilton, Toronto, and Niagara Region. These specialized drywall types prevent moisture damage and mold growth. We also install cement board behind tile in showers and tub surrounds for maximum water protection."
    },
    {
      question: "Can you install soundproof drywall for noise reduction?",
      answer: "Yes. We install soundproof drywall systems including double-layer drywall, sound-dampening clips, resilient channels, and acoustic insulation for home theaters, music rooms, bedrooms, and multi-unit buildings in Niagara, Hamilton, and Toronto. These systems significantly reduce sound transmission between rooms and floors, meeting STC (Sound Transmission Class) ratings as required."
    },
    {
      question: "Do you provide drywall finishing for new construction and renovations?",
      answer: "Yes. Blue Knight Contracting provides drywall installation and finishing for both new construction builds and renovation projects across Niagara Region, Hamilton, and Toronto GTA. Whether you're building a new home, finishing a basement, renovating a kitchen, or adding an addition, our experienced drywall finishers deliver smooth, paint-ready surfaces on schedule."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Drywall & Finishing Services - Niagara Region, Hamilton, Toronto",
    description: "Professional drywall installation, taping, mudding, and finishing services in Niagara Region, Hamilton, and Toronto GTA for residential and commercial properties.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction"
    },
    serviceType: "Drywall Installation & Finishing",
    areaServed: ["Niagara Region", "Hamilton", "Toronto", "Ontario"]
  };

  const drywallServices = [
    {
      title: "Residential Drywall",
      description: "Complete drywall installation and finishing for homes, condos, and apartments.",
      icon: Home,
      services: [
        "New home drywall installation",
        "Basement finishing",
        "Room additions",
        "Ceiling installation",
        "Wall repairs",
        "Renovation drywall work"
      ]
    },
    {
      title: "Commercial Drywall",
      description: "Professional drywall services for commercial and industrial spaces.",
      icon: Building,
      services: [
        "Office build-outs",
        "Retail space drywall",
        "Restaurant renovations",
        "Medical facility build-outs",
        "Warehouse office spaces",
        "Fire-rated assemblies"
      ]
    },
    {
      title: "Drywall Installation",
      description: "Expert hanging of drywall sheets on walls and ceilings.",
      icon: Hammer,
      services: [
        "Standard 1/2\" drywall",
        "5/8\" fire-rated drywall",
        "Moisture-resistant drywall",
        "Soundproof drywall systems",
        "Ceiling installation",
        "Curved walls"
      ]
    },
    {
      title: "Taping & Mudding",
      description: "Professional taping and multiple-coat mudding for smooth finishes.",
      icon: Ruler,
      services: [
        "Seam taping",
        "Corner bead installation",
        "Multi-coat mudding",
        "Skim coating",
        "Level 4 & 5 finishes",
        "Smooth finish preparation"
      ]
    },
    {
      title: "Texture & Finishing",
      description: "Custom textures and finishing to match your design preferences.",
      icon: Paintbrush,
      services: [
        "Orange peel texture",
        "Knockdown texture",
        "Skip trowel texture",
        "Smooth finish",
        "Popcorn removal",
        "Texture matching"
      ]
    },
    {
      title: "Repairs & Patches",
      description: "Expert repair of damaged drywall with seamless blending.",
      icon: Shield,
      services: [
        "Hole repairs",
        "Crack repair",
        "Water damage restoration",
        "Impact damage repair",
        "Texture matching",
        "Seamless patching"
      ]
    }
  ];

  const customerReviews = [
    {
      name: "James R.",
      location: "St. Catharines, Niagara Region",
      rating: 5,
      review: "Blue Knight finished our entire basement with perfect drywall work. The taping and mudding is flawless—you can't see any seams! Excellent craftsmanship in St. Catharines."
    },
    {
      name: "Patricia K.",
      location: "Hamilton, ON",
      rating: 5,
      review: "Had our Hamilton home's main floor renovated with new drywall throughout. The Level 5 finish looks amazing under our pot lights. Professional team and great attention to detail!"
    },
    {
      name: "Daniel M.",
      location: "Toronto, GTA",
      rating: 5,
      review: "Blue Knight installed soundproof drywall in our Toronto condo between units. Noise reduction is incredible! Clean work, on time, and they cleaned up thoroughly every day."
    },
    {
      name: "Rebecca S.",
      location: "Niagara Falls, ON",
      rating: 5,
      review: "Repaired water-damaged drywall in our Niagara Falls bathroom. They matched the existing knockdown texture perfectly—you'd never know there was damage. Highly recommend!"
    },
    {
      name: "Mark T.",
      location: "Mississauga, GTA",
      rating: 5,
      review: "Commercial drywall build-out for our Mississauga office. Blue Knight handled fire-rated walls, smooth finish throughout, and finished on schedule. Very professional company."
    },
    {
      name: "Linda W.",
      location: "Welland, Niagara Region",
      rating: 5,
      review: "Finished our Welland home addition with beautiful smooth drywall. The mudding and sanding created perfect walls ready for paint. Fair pricing and excellent quality work!"
    }
  ];

  const serviceAreas = [
    { name: "Niagara Region", link: "/locations", featured: true },
    { name: "Hamilton", link: "/hamilton", featured: true },
    { name: "Toronto GTA", link: "/toronto", featured: true },
    { name: "St. Catharines", link: "/locations", featured: false },
    { name: "Niagara Falls", link: "/locations", featured: false },
    { name: "Welland", link: "/locations", featured: false },
    { name: "Mississauga", link: "/mississauga", featured: false },
    { name: "Burlington", link: "/locations", featured: false },
    { name: "Oakville", link: "/locations", featured: false },
    { name: "Grimsby", link: "/locations", featured: false },
    { name: "Brampton", link: "/locations", featured: false },
    { name: "Vaughan", link: "/locations", featured: false }
  ];

  return (
    <>
      <SEOMeta
        title="Drywall Contractor — Installation & Finishing | Niagara • Hamilton • GTA"
        description="Expert drywall installation, mud and tape, texture and repairs in Niagara, Hamilton & the GTA. Smooth finishes, ready for paint."
        keywords="drywall contractor Niagara, Hamilton drywall finishing, GTA drywall services, taping, mud finishing, ceiling repair, wall patching"
        canonicalUrl="https://blueknightcontracting.ca/services/drywall"
      />
      
      <LocalBusinessSchema
        locationName="Niagara Region"
        addressLocality="St. Catharines"
        addressRegion="ON"
        description="Professional drywall installation, taping, mudding, and finishing services in Niagara Region, Hamilton, and Toronto GTA for residential and commercial properties with expert craftsmanship."
        services={[
          "Drywall Installation",
          "Drywall Finishing",
          "Taping & Mudding",
          "Texture Application",
          "Drywall Repair",
          "Basement Drywall",
          "Commercial Drywall",
          "Soundproof Drywall",
          "Fire-Rated Drywall",
          "Moisture-Resistant Drywall"
        ]}
      />
      
      <SchemaMarkup 
        type="Service"
        data={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Professional Drywall & Finishing Services
                  <br />
                  <span className="text-3xl lg:text-4xl">Niagara Region, Hamilton & Toronto GTA</span>
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting provides expert drywall installation, taping, mudding, and finishing services across Niagara Region, Hamilton, and Toronto GTA. From new construction to renovations, we deliver smooth, paint-ready walls and ceilings with professional craftsmanship.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Phone className="h-5 w-5 mr-2" />
                    Call for Consultation
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={drywallFinishing} 
                  alt="Professional drywall finisher applying smooth finish to walls in Niagara Region home renovation"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Comprehensive Drywall Services
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Expert drywall installation and finishing for residential and commercial projects in Niagara Region, Hamilton, and Toronto GTA
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {drywallServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured Drywall Projects
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Professional drywall installation and finishing across Ontario
              </p>
            </div>

            <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={drywallInstallation} 
                    alt="Drywall installation in progress - hanging sheets on wall frames in Niagara Region construction project"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Drywall Installation</h3>
                  <p className="text-muted-foreground">
                    Expert hanging of drywall sheets on walls and ceilings
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={drywallSmooth} 
                    alt="Smooth finished drywall ceiling with professional taping and mudding ready for paint in Hamilton home"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Smooth Finishing</h3>
                  <p className="text-muted-foreground">
                    Level 4 and Level 5 finishes for flawless paint-ready surfaces
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={drywallTools} 
                    alt="Professional drywall finishing tools including taping knives, mud pan, and compound for Toronto renovation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Professional Tools</h3>
                  <p className="text-muted-foreground">
                    Using industry-leading tools and materials for quality results
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight for Drywall?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Expert Finishers</h3>
                <p className="text-muted-foreground">
                  Skilled drywall finishers with years of experience
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Materials</h3>
                <p className="text-muted-foreground">
                  Premium drywall sheets and joint compounds
                </p>
              </Card>

              <Card className="text-center p-6">
                <Clock className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">On-Time Completion</h3>
                <p className="text-muted-foreground">
                  Reliable scheduling with timely project completion
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Smooth Finishes</h3>
                <p className="text-muted-foreground">
                  Flawless Level 4 & 5 finishes ready for painting
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Customer Reviews */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                What Our Customers Say
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Real reviews from satisfied drywall customers in Niagara Region, Hamilton, and Toronto GTA
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {customerReviews.map((review, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <Quote className="h-8 w-8 text-primary/20 mb-2" />
                  <p className="text-muted-foreground mb-4 italic">
                    "{review.review}"
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-semibold">{review.name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {review.location}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Drywall Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Proudly serving Niagara Region, Hamilton, Toronto GTA, and surrounding areas with professional drywall installation and finishing
              </p>
            </div>

            <div className="mb-12">
              <h3 className="text-2xl font-semibold mb-6 text-center">Primary Service Areas</h3>
              <div className="grid md:grid-cols-3 gap-6">
                {serviceAreas.filter(area => area.featured).map((area, index) => (
                  <Card key={index} className="p-6 text-center bg-primary text-white hover:shadow-lg transition-shadow">
                    <MapPin className="h-8 w-8 mx-auto mb-3" />
                    <Link to={area.link} className="font-semibold text-xl hover:underline">
                      {area.name}
                    </Link>
                    <p className="text-sm mt-2 opacity-90">Professional Drywall Services</p>
                  </Card>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-6 text-center">Additional Service Areas</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {serviceAreas.filter(area => !area.featured).map((area, index) => (
                  <Card key={index} className="p-4 text-center hover:shadow-md transition-shadow">
                    <MapPin className="h-5 w-5 text-primary mx-auto mb-2" />
                    <Link to={area.link} className="font-medium hover:text-primary hover:underline">
                      {area.name}
                    </Link>
                  </Card>
                ))}
              </div>
            </div>

            <div className="mt-12 text-center">
              <p className="text-muted-foreground mb-4">
                Need drywall services in another area? <Link to="/contact" className="text-primary hover:underline font-medium">Contact us</Link> to discuss your project.
              </p>
              <p className="text-sm text-muted-foreground">
                Related Services: <Link to="/services/carpenter" className="text-primary hover:underline">Carpenter Services</Link> | <Link to="/services/painting" className="text-primary hover:underline">Painting Services</Link> | <Link to="/services/flooring" className="text-primary hover:underline">Flooring Installation</Link> | <Link to="/services/electrician" className="text-primary hover:underline">Electrician Services</Link>
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about drywall installation and finishing in Niagara Region, Hamilton, and Toronto GTA
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Ready for Professional Drywall Installation?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting today for expert drywall installation, taping, mudding, and finishing services in Niagara Region, Hamilton, and Toronto GTA. Free quotes available!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>(416) 555-BLUE</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>info@blueknightcontracting.ca</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving Niagara, Hamilton & Toronto</span>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary">
                <Link to="/contact">Get Free Drywall Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                <Phone className="h-5 w-5 mr-2" />
                Schedule Consultation
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default DrywallServices;
